import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaSearch, FaCalendarDay, FaCalendarWeek, FaClock, FaCheck, FaExclamationCircle, FaEdit, FaTrash, FaListAlt, FaCalendar } from 'react-icons/fa';
import taskService from '../services/taskService';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { cn } from '../utils/cn';

const TaskListModern = ({ refreshTrigger, onTaskToggle, onTaskEdit, onTaskDelete }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentFilter, setCurrentFilter] = useState('Today');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filterCounts, setFilterCounts] = useState({
    Today: 0,
    ThisWeek: 0,
    Waitlist: 0,
    Delayed: 0,
    Completed: 0
  });

  // Corrected fetch tasks method to properly set pagination
  const fetchFilteredTasks = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      if (searchTerm) {
        response = await taskService.searchTasks(searchTerm);
        setTasks(response.tasks || []);
        setPagination({
          currentPage: response.pagination?.current_page || 1,
          totalPages: response.pagination?.total_pages || 1,
          totalItems: response.pagination?.total_items || (response.tasks?.length || 0),
          limit: response.pagination?.limit || 20,
          hasNextPage: response.pagination?.has_next_page || false,
          hasPreviousPage: response.pagination?.has_previous_page || false
        });
      } else {
        response = await taskService.getTasks({ 
          taskType: currentFilter,
          page
        });
        setTasks(response.tasks || []);
        setPagination({
          currentPage: response.pagination?.current_page || page,
          totalPages: response.pagination?.total_pages || 1,
          totalItems: response.pagination?.total_items || (response.tasks?.length || 0),
          limit: response.pagination?.limit || 20,
          hasNextPage: response.pagination?.has_next_page || false,
          hasPreviousPage: response.pagination?.has_previous_page || page > 1
        });
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentFilter, searchTerm]);

  // Fetch counts for each filter
  const fetchFilterCounts = useCallback(async () => {
    try {
      //console.log('Fetching filter counts...');
      const filterMapping = {
        'Today': 'Today',
        'ThisWeek': 'ThisWeek',
        'Waitlist': 'Waitlist',
        'Delayed': 'Delayed',
        'Completed': 'Completed'
      };

      const newCounts = {}; // Use a temporary object

      const countPromises = Object.keys(filterMapping).map(filter => 
        taskService.getTasks({ 
          taskType: filterMapping[filter],
          page: 1,
          limit: 1 
        })
      );
      
      const responses = await Promise.allSettled(countPromises); // Use allSettled
      
      Object.keys(filterMapping).forEach((filter, index) => {
        const result = responses[index];
        if (result.status === 'fulfilled') {
          const response = result.value;
          //console.log(`Count response for ${filter}:`, response);
          newCounts[filter] = response?.pagination?.totalItems ?? 0; // Use nullish coalescing
        } else {
          console.error(`Failed to fetch count for ${filter}:`, result.reason);
          newCounts[filter] = 0; // Default to 0 on error for this specific filter
        }
      });
      
      //console.log("Setting new counts:", newCounts);
      setFilterCounts(newCounts); // Update state once with all counts

    } catch (err) {
      // This catch might not be strictly necessary with allSettled, but good practice
      console.error('General error fetching task counts:', err);
    }
  }, []); // Keep dependencies empty for now to avoid loops

  // Optimize to prevent duplicate API calls 
  useEffect(() => {
    //console.log('Filter changed or refresh triggered');
    // Reset pagination when filter changes
    if (currentFilter) {
      fetchFilteredTasks(1);
    }
  }, [currentFilter, searchTerm, refreshTrigger, fetchFilteredTasks]);

  // Separate effect for pagination changes to avoid duplicate calls
  useEffect(() => {
    // Only fetch if pagination changed but not from a filter change
    if (pagination.currentPage > 1) {
      fetchFilteredTasks(pagination.currentPage);
    }
  }, [pagination.currentPage, fetchFilteredTasks]);

  // Fetch counts separately and less frequently
  useEffect(() => {
    //console.log('Fetching counts on refresh or mount');
    fetchFilterCounts();
  }, [fetchFilterCounts, refreshTrigger]);

  // Handle filter change
  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setSearchTerm('');
    // Reset to page 1 when filter changes
    setPagination(prev => ({ ...prev, currentPage: 1 })); 
    // Counts will be refetched by the useEffect above
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Format due date
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  // Get task icon based on status
  const getTaskStatusIcon = (task) => {
    if (task.is_completed) return <FaCheck className="h-4 w-4" />;
    if (task.due_date && new Date(task.due_date) < new Date()) return <FaExclamationCircle className="h-4 w-4" />;
    return <FaClock className="h-4 w-4" />;
  };

  // Determine task status
  const getTaskStatus = (task) => {
    if (task.is_completed) return 'completed';
    if (task.due_date && new Date(task.due_date) < new Date()) return 'overdue';
    return 'pending';
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  // Render task list or appropriate message
  const renderContent = () => {
    if (loading) {
      return <div className="py-8 text-center text-muted-foreground">Loading tasks...</div>;
    }
    
    if (error) {
      return <div className="py-8 text-center text-muted-foreground">{error}</div>;
    }
    
    if (!tasks || tasks.length === 0) {
      return <div className="py-8 text-center text-muted-foreground">No tasks found.</div>;
    }
    
    const handleTaskToggle = async (task) => {
      try {
        await taskService.toggleTaskCompletion(task.id);
        // Refresh the current view and counts
        await fetchFilteredTasks(pagination.currentPage); 
        await fetchFilterCounts(); // Refetch counts after toggle
      } catch (error) {
        console.error('Error toggling task completion:', error);
      }
    };
    
    return (
      <>
        {tasks.map(task => (
          <div key={task.id} className="py-4 px-4 border-b border-border flex items-center hover:bg-accent/5 transition-colors">
            <div className="mr-3 flex-shrink-0">
              <Checkbox 
                checked={task.is_completed}
                onCheckedChange={() => onTaskToggle ? onTaskToggle(task) : handleTaskToggle(task)}
              />
            </div>
            <div className="flex-1">
              <div className={cn(
                "font-medium mb-1", 
                task.is_completed && "line-through text-muted-foreground"
              )}>
                {task.title}
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                {task.due_date && (
                  <div className="flex items-center">
                    <FaClock className="mr-1 h-3 w-3" />
                    {formatDate(task.due_date)}
                  </div>
                )}
                {task.category && (
                  <div className="inline-block py-0.5 px-2 rounded-full text-xs" 
                    style={{ 
                      backgroundColor: task.category.color || '#f0f0f0',
                      color: task.category.textColor || '#333'
                    }}
                  >
                    {task.category.name}
                  </div>
                )}
                {task.is_high_priority && (
                  <div className="flex items-center text-destructive font-medium">
                    <FaExclamationCircle className="mr-1 h-3 w-3" />
                    High Priority
                  </div>
                )}
                {task.recurring_formula && (
                  <div className="flex items-center">
                    <FaCalendarDay className="mr-1 h-3 w-3" />
                    Recurring
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 ml-2">
              {onTaskEdit && (
                <Button variant="ghost" size="icon" onClick={() => onTaskEdit(task)}>
                  <FaEdit className="h-4 w-4" />
                </Button>
              )}
              {onTaskDelete && (
                <Button variant="ghost" size="icon" onClick={() => onTaskDelete(task.id)}>
                  <FaTrash className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;
    
    const currentPage = pagination.currentPage;
    const totalPages = pagination.totalPages;
    
    return (
      <div className="flex justify-center gap-2 py-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handlePageChange(1)} 
          disabled={currentPage === 1}
        >
          First
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handlePageChange(currentPage - 1)} 
          disabled={!pagination.hasPreviousPage}
        >
          Previous
        </Button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          // Show a window of 5 pages centered around current page
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }
          
          return (
            <Button 
              key={pageNum}
              variant={pageNum === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </Button>
          );
        })}
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handlePageChange(currentPage + 1)} 
          disabled={!pagination.hasNextPage}
        >
          Next
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handlePageChange(totalPages)} 
          disabled={currentPage === totalPages}
        >
          Last
        </Button>
      </div>
    );
  };

  // Get filter icon based on filter type
  const getFilterIcon = (filter) => {
    switch (filter) {
      case 'Today':
        return <FaCalendarDay className="mr-2" />;
      case 'ThisWeek':
        return <FaCalendarWeek className="mr-2" />;
      case 'Waitlist':
        return <FaListAlt className="mr-2" />;
      case 'Delayed':
        return <FaExclamationCircle className="mr-2" />;
      case 'Completed':
        return <FaCheck className="mr-2" />;
      case 'Scheduled':
        return <FaCalendar className="mr-2" />;
      default:
        return <FaCalendarDay className="mr-2" />;
    }
  };

  // Get display name for filter
  const getFilterDisplayName = (filter) => {
    switch (filter) {
      case 'Today':
        return 'Today';
      case 'ThisWeek':
        return 'This Week';
      case 'Waitlist': 
        return 'Waitlist';
      case 'Delayed':
        return 'Overdue';
      case 'Completed':
        return 'Completed';
      default:
        return filter;
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center mb-6 flex-wrap gap-4">
        <div className="relative flex-1 min-w-[250px]">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.keys(filterCounts).map((filter) => (
            <Button
              key={filter}
              variant={currentFilter === filter ? "default" : "outline"}
              onClick={() => handleFilterChange(filter)}
              className="flex items-center"
            >
              {getFilterIcon(filter)}
              {getFilterDisplayName(filter)}
              {filterCounts[filter] > 0 && (
                <span className="ml-2 bg-primary-foreground text-primary rounded-full px-2 py-0.5 text-xs">
                  {filterCounts[filter]}
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {renderContent()}
        </CardContent>
      </Card>
      
      {renderPagination()}
    </div>
  );
};

export default TaskListModern; 